import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import type {
  RawAnswer,
  RawQuestionStats,
  RawVariable,
} from '@/features/content/useComponent';
import { componentQueryKey } from '@/features/content/useComponent';
import { confirmsStoredSubmission } from '@/pwa/storedSubmission';
import { createRequestId } from '@/pwa/syncMessages';
import { markPwaHomeworkEngagement } from '@/pwa/useInstallPrompt';

export interface RandomAnswerData {
  currentRandomExactAnswer?: string;
  currentRandomNumericalAnswer?: number;
  currentRandomNumericalAnswerExtraExponent?: number | null;
  hasCurrentRandomNumericalAnswerExtraExponent?: boolean;
  isRandomized?: boolean;
  // The resolved variable values (name + current_value) that produce this
  // answer, persisted so display and grading stay a matched pair. See
  // Answer.variables and the normalizer's resolveNumericalRandomAnswer.
  variables?: RawVariable[];
}

export type ExistingQuestionStat = RawQuestionStats;
export type QuestionSubmissionSectionKind = 'homework' | 'preLecture';

export interface QuestionSubmissionSectionContext {
  allQuestionStats: ExistingQuestionStat[];
  hasExistingSectionStats: boolean;
  homeworkHasExistingQuestionStats?: boolean;
  homeworkNumAttempts?: number;
  homeworkQuestionStats?: ExistingQuestionStat[];
  homeworkTotalScore?: number;
  preLectureHasExistingQuestionStats?: boolean;
  preLectureNumAttempts?: number;
  preLectureQuestionStats?: ExistingQuestionStat[];
  preLectureTotalScore?: number;
  randomizedQuestionIds: Record<string, boolean>;
  randomAnswers: Record<string, RandomAnswerData>;
  sectionKind?: QuestionSubmissionSectionKind;
  sectionNumAttempts: number;
  sectionStatsId: string;
  sectionTotalScore: number;
}

export type NormalizedAnswerInput =
  | {
      fileUploadUrl: string;
      kind: 'aiUpload';
    }
  | {
      currentRandomExactAnswer?: string;
      kind: 'exactAnswer';
      value: string;
    }
  | {
      currentRandomExactAnswer?: string;
      currentRandomNumericalAnswer?: number;
      currentRandomNumericalAnswerExtraExponent?: number;
      kind: 'multipleChoice';
      selectedIndex?: number;
    }
  | {
      currentRandomNumericalAnswer?: number;
      currentRandomNumericalAnswerExtraExponent?: number;
      kind: 'numerical';
      value: string;
    }
  | {
      kind: 'selectAll';
      // Every option the student ticked. answer_index is already repeated on the
      // wire, so a multiple-choice answer stays a one-element list and nothing
      // about the payload shape changes.
      selectedIndices: number[];
    };

export interface SubmitQuestionAnswerInput
  extends QuestionSubmissionSectionContext {
  answer: NormalizedAnswerInput;
  componentId: string;
  courseId: string;
  mode: 'check' | 'save';
  parentQuestionId?: string;
  questionId: string;
  sectionId: string;
}

interface QuestionStatPayload {
  answer: Record<string, unknown>;
  current_num_attempts?: number;
  id: string;
  needs_grading?: boolean;
  only_check_this_question?: boolean;
  question_set_stats?: {
    question_stats: QuestionStatPayload[];
  };
  save_only?: boolean;
  // Only the question actually being submitted carries a score; placeholder
  // entries omit it so a stale client snapshot can never regress the stored one.
  score?: number;
}

interface SubmissionRequestPayload {
  component: {
    // Section totals and attempt counters are intentionally NOT sent: the server
    // maintains them (grading recomputes totals and increments attempts), and the
    // client's snapshot may be older than the stored state (another device/tab,
    // the post-save refetch window, an offline-queue replay).
    section_stats: Array<{
      id: string;
      pre_lecture_question_stats?: QuestionStatPayload[];
      question_stats: QuestionStatPayload[];
    }>;
    uid: string;
  };
  component_id: string;
  course_id: string;
  grade_component?: boolean;
  only_score_pre_lecture?: boolean;
  user: {
    id: string;
  };
  user_id: string;
}

export interface QuestionSubmissionResult {
  queued: boolean;
  requestId: string;
}

interface BuildQuestionStatPayloadResult {
  didUpdate: boolean;
  payload: QuestionStatPayload;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toQuestionStatsArray(value: unknown): ExistingQuestionStat[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as ExistingQuestionStat[];
}

function getNestedQuestionStats(questionStat: ExistingQuestionStat) {
  return toQuestionStatsArray(
    questionStat.question_set_stats?.question_stats ??
      questionStat.question_set_stats?.questionStats ??
      questionStat.questionSetStats?.question_stats ??
      questionStat.questionSetStats?.questionStats,
  );
}

function parseNumericalInput(value: string) {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return {};
  }

  // Send the FULL numeric value (scientific notation included) in
  // numerical_answer. We deliberately do NOT split it into a mantissa plus a
  // numerical_answer_extra_exponent: a JS double represents the full range of
  // realistic answers, and the backend ultimately compares in float32, so the
  // split gains no precision. The split also caused a grading bug — the backend
  // only re-applied the exponent when it was POSITIVE, so a negative exponent
  // (e.g. "7.94e-8" → mantissa 7.94, exponent -8) was dropped and the mantissa
  // was graded against the true value. Number() parses "7.94e-8" directly to the
  // correct double.
  const numericValue = Number(trimmed);

  return Number.isFinite(numericValue)
    ? {
        numerical_answer: numericValue,
      }
    : {};
}

function mergeRandomAnswerData(
  answerPayload: Record<string, unknown>,
  randomAnswer?: RandomAnswerData,
) {
  if (!randomAnswer?.isRandomized) {
    return { ...answerPayload };
  }

  const nextPayload = { ...answerPayload };

  if (randomAnswer?.currentRandomExactAnswer !== undefined) {
    nextPayload.current_random_exact_answer = randomAnswer.currentRandomExactAnswer;
  }

  if (randomAnswer?.currentRandomNumericalAnswer !== undefined) {
    nextPayload.current_random_numerical_answer =
      randomAnswer.currentRandomNumericalAnswer;
    if (randomAnswer.hasCurrentRandomNumericalAnswerExtraExponent) {
      nextPayload.current_random_numerical_answer_extra_exponent =
        randomAnswer.currentRandomNumericalAnswerExtraExponent ?? null;
    }
  }

  // Persist the variable values that produce this answer. The backend replaces
  // the stored answer wholesale on a real submission, so re-sending these keeps
  // the student's displayed numbers and graded answer a matched pair (and avoids
  // erasing previously-stored variables). Only present for numerical randomized
  // questions where the normalizer had a safe matched pair to persist.
  if (randomAnswer?.variables !== undefined && randomAnswer.variables.length > 0) {
    nextPayload.variables = randomAnswer.variables;
  }

  return nextPayload;
}



function getQuestionScore(questionStat: ExistingQuestionStat | null | undefined) {
  return normalizeNumber(questionStat?.score) ?? 0;
}

function getQuestionAttempts(
  questionStat: ExistingQuestionStat | null | undefined,
) {
  return normalizeNumber(
    questionStat?.current_num_attempts ?? questionStat?.currentNumAttempts,
  );
}

function withPreservedQuestionAttempts(
  payload: QuestionStatPayload,
  questionStat: ExistingQuestionStat | null | undefined,
  fallbackAttempts?: number,
): QuestionStatPayload {
  const attempts = getQuestionAttempts(questionStat) ?? fallbackAttempts;

  return attempts === undefined
    ? payload
    : {
        ...payload,
        current_num_attempts: attempts,
      };
}

function resolveRandomAnswerData(
  answer: NormalizedAnswerInput,
  existingRandomAnswer?: RandomAnswerData,
): RandomAnswerData | undefined {
  if (answer.kind === 'aiUpload') {
    return undefined;
  }

  const nextRandomAnswer: RandomAnswerData = {
    ...existingRandomAnswer,
  };

  if (answer.kind === 'exactAnswer' && answer.currentRandomExactAnswer !== undefined) {
    nextRandomAnswer.currentRandomExactAnswer = answer.currentRandomExactAnswer;
    nextRandomAnswer.isRandomized = true;
  }

  // Select-all carries no randomized value: the answer is a set of chosen
  // option indices, not something computed from a variable.
  if (answer.kind !== 'exactAnswer' && answer.kind !== 'selectAll') {
    if (answer.currentRandomNumericalAnswer !== undefined) {
      nextRandomAnswer.currentRandomNumericalAnswer =
        answer.currentRandomNumericalAnswer;
      nextRandomAnswer.isRandomized = true;
    }

    if (answer.currentRandomNumericalAnswerExtraExponent !== undefined) {
      nextRandomAnswer.currentRandomNumericalAnswerExtraExponent =
        answer.currentRandomNumericalAnswerExtraExponent;
      nextRandomAnswer.hasCurrentRandomNumericalAnswerExtraExponent = true;
      nextRandomAnswer.isRandomized = true;
    }
  }

  return nextRandomAnswer.isRandomized ? nextRandomAnswer : undefined;
}

export function buildAnswerPayload(
  answer: NormalizedAnswerInput,
  existingRandomAnswer?: RandomAnswerData,
) {
  const resolvedRandomAnswer = resolveRandomAnswerData(
    answer,
    existingRandomAnswer,
  );

  if (answer.kind === 'multipleChoice') {
    return mergeRandomAnswerData(
      answer.selectedIndex === undefined
        ? {}
        : {
            answer_index: [answer.selectedIndex],
          },
      resolvedRandomAnswer,
    );
  }

  if (answer.kind === 'selectAll') {
    return mergeRandomAnswerData(
      { answer_index: answer.selectedIndices },
      resolvedRandomAnswer,
    );
  }

  if (answer.kind === 'aiUpload') {
    return {
      file_upload_url: answer.fileUploadUrl,
    };
  }

  if (answer.kind === 'exactAnswer') {
    return mergeRandomAnswerData(
      {
        exact_answer: answer.value,
      },
      resolvedRandomAnswer,
    );
  }

  return mergeRandomAnswerData(
    parseNumericalInput(answer.value),
    resolvedRandomAnswer,
  );
}

export function buildAnswerPayloadWithoutRandom(answer: NormalizedAnswerInput) {
  if (answer.kind === 'multipleChoice') {
    return answer.selectedIndex === undefined
      ? {}
      : {
          answer_index: [answer.selectedIndex],
        };
  }

  if (answer.kind === 'selectAll') {
    return { answer_index: answer.selectedIndices };
  }

  if (answer.kind === 'aiUpload') {
    return {
      file_upload_url: answer.fileUploadUrl,
    };
  }

  if (answer.kind === 'exactAnswer') {
    return {
      exact_answer: answer.value,
    };
  }

  return parseNumericalInput(answer.value);
}

function getUpdatedQuestionFlags(
  input: SubmitQuestionAnswerInput,
): Pick<
  QuestionStatPayload,
  'needs_grading' | 'only_check_this_question' | 'save_only'
> {
  if (input.mode === 'check') {
    return {
      needs_grading: true,
      only_check_this_question: true,
      save_only: false,
    };
  }

  return getSubmissionSectionKind(input) === 'preLecture'
    ? {
        needs_grading: true,
        save_only: true,
      }
    : {
        needs_grading: false,
        save_only: true,
      };
}

function buildFallbackQuestionStatPayload(
  input: SubmitQuestionAnswerInput,
): QuestionStatPayload {
  const isRandomizedQuestion =
    input.randomizedQuestionIds[input.questionId] ?? false;
  const questionPayload: QuestionStatPayload = {
    answer:
      input.mode === 'check' && !isRandomizedQuestion
        ? buildAnswerPayloadWithoutRandom(input.answer)
        : buildAnswerPayload(
            input.answer,
            input.randomAnswers[input.questionId],
          ),
    id: input.questionId,
    score: 0,
  };
  const questionPayloadWithAttempts = withPreservedQuestionAttempts(
    questionPayload,
    null,
    0,
  );

  if (!input.parentQuestionId) {
    return {
      ...questionPayloadWithAttempts,
      ...getUpdatedQuestionFlags(input),
    };
  }

  return {
    answer: {},
    id: input.parentQuestionId,
    score: 0,
    question_set_stats: {
      question_stats: [
        {
          ...questionPayload,
          ...getUpdatedQuestionFlags(input),
          current_num_attempts: questionPayloadWithAttempts.current_num_attempts,
        },
      ],
    },
  };
}

function buildQuestionStatPayload(
  questionStat: ExistingQuestionStat,
  input: SubmitQuestionAnswerInput,
  path: number[],
  options: {
    allowUpdate: boolean;
  } = { allowUpdate: true },
): BuildQuestionStatPayloadResult {
  const questionId =
    normalizeIdentifier(questionStat.id) ??
    (path.length > 0 ? `question-${path.join('-')}` : input.questionId);
  const existingNestedStats = getNestedQuestionStats(questionStat);
  let didUpdate = false;
  let nestedPayload: QuestionStatPayload[] | undefined;

  if (existingNestedStats.length > 0) {
    const nestedResults = existingNestedStats.map((nestedStat, index) =>
      buildQuestionStatPayload(nestedStat, input, [...path, index + 1], options),
    );
    nestedPayload = nestedResults.map((result) => result.payload);
    didUpdate = nestedResults.some((result) => result.didUpdate);
  }

  if (options.allowUpdate && questionId === input.questionId) {
    const isRandomizedQuestion = input.randomizedQuestionIds[questionId] ?? false;

    return {
      didUpdate: true,
      payload: withPreservedQuestionAttempts(
        {
          answer:
            input.mode === 'check' && !isRandomizedQuestion
              ? buildAnswerPayloadWithoutRandom(input.answer)
              : buildAnswerPayload(
                  input.answer,
                  input.randomAnswers[questionId],
                ),
          id: questionId,
          score: getQuestionScore(questionStat),
          ...getUpdatedQuestionFlags(input),
          ...(nestedPayload && nestedPayload.length > 0
            ? {
                question_set_stats: {
                  question_stats: nestedPayload,
                },
              }
            : {}),
        },
        questionStat,
        0,
      ),
    };
  }

  // A question that is NOT being submitted is sent as a pure positional
  // placeholder: {id, answer: {}}. It asserts positional presence for the
  // authoritative full-list merge but carries NO response fields — no answer
  // content, no score, no attempts, no grading flags — so the server's stored
  // data for this question, which may be NEWER than this client's snapshot
  // (another device or tab, the post-save refetch window, an offline-queue
  // replay), can never be regressed by a save of a different question. The
  // empty answer is also safe against older backends, whose merge has never
  // let an empty answer overwrite a stored one. The nested list is included
  // only when the submitted question is one of this parent's parts (it must
  // then be full-length so the positional nested merge doesn't truncate).
  return {
    didUpdate,
    payload: {
      answer: {},
      id: questionId,
      ...(didUpdate && nestedPayload && nestedPayload.length > 0
        ? {
            question_set_stats: {
              question_stats: nestedPayload,
            },
          }
        : {}),
    },
  };
}

function buildQuestionStatsPayload(
  input: SubmitQuestionAnswerInput,
  questionStats: ExistingQuestionStat[],
  options: {
    allowUpdate: boolean;
    appendFallback: boolean;
  },
) {
  const results = questionStats.map((questionStat, index) =>
    buildQuestionStatPayload(questionStat, input, [index + 1], {
      allowUpdate: options.allowUpdate,
    }),
  );
  const payload = results.map((result) => result.payload);
  const didUpdate = results.some((result) => result.didUpdate);

  return options.appendFallback && !didUpdate
    ? [...payload, buildFallbackQuestionStatPayload(input)]
    : payload;
}

export function buildSectionQuestionStatsPayload(
  input: SubmitQuestionAnswerInput,
) {
  return buildQuestionStatsPayload(input, input.allQuestionStats, {
    allowUpdate: true,
    appendFallback: true,
  });
}

function getSubmissionSectionKind(
  input: QuestionSubmissionSectionContext,
): QuestionSubmissionSectionKind {
  return input.sectionKind ?? 'homework';
}

function getQuestionStatsForKind(
  input: SubmitQuestionAnswerInput,
  kind: QuestionSubmissionSectionKind,
) {
  if (kind === 'preLecture') {
    return input.preLectureQuestionStats ?? (
      getSubmissionSectionKind(input) === 'preLecture'
        ? input.allQuestionStats
        : []
    );
  }

  return input.homeworkQuestionStats ?? (
    getSubmissionSectionKind(input) === 'homework'
      ? input.allQuestionStats
      : []
  );
}




export function buildSubmissionRequestPayload(
  input: SubmitQuestionAnswerInput,
  kuduUserId: string,
): SubmissionRequestPayload {
  const targetKind = getSubmissionSectionKind(input);
  const homeworkQuestionStats = getQuestionStatsForKind(input, 'homework');
  const preLectureQuestionStats = getQuestionStatsForKind(input, 'preLecture');
  const hasPreLectureQuestionStats = preLectureQuestionStats.length > 0;
  // Section totals and attempt counters are deliberately omitted: they would be
  // computed from this client's snapshot, which may be older than the stored
  // state, and the server maintains both (grading recomputes totals and
  // increments attempt counters from the stored truth).
  const sectionStats = {
    id: input.sectionStatsId,
    question_stats: buildQuestionStatsPayload(input, homeworkQuestionStats, {
      allowUpdate: targetKind === 'homework',
      appendFallback: targetKind === 'homework',
    }),
    ...(hasPreLectureQuestionStats
      ? {
          pre_lecture_question_stats: buildQuestionStatsPayload(
            input,
            preLectureQuestionStats,
            {
              allowUpdate: targetKind === 'preLecture',
              appendFallback: targetKind === 'preLecture',
            },
          ),
        }
      : {}),
  };

  return {
    component: {
      section_stats: [sectionStats],
      uid: input.componentId,
    },
    component_id: input.componentId,
    course_id: input.courseId,
    ...(input.mode === 'check' || targetKind === 'preLecture'
      ? { grade_component: true }
      : {}),
    ...(targetKind === 'preLecture' ? { only_score_pre_lecture: true } : {}),
    user: {
      id: kuduUserId,
    },
    user_id: kuduUserId,
  };
}

/**
 * Maps a submission failure to a generic, user-facing message.
 *
 * Backend errors reach the client with the raw HTTP error body as
 * `error.message` (internal error strings, request payload dumps, datastore
 * errors). That text must never render in the UI — display surfaces pick a
 * fixed message by error kind instead.
 */
export function getSubmissionErrorMessage(
  error: unknown,
  mode?: 'check' | 'save',
): string {
  const kind =
    typeof error === 'object' && error !== null && 'kind' in error
      ? (error as ApiError).kind
      : undefined;

  switch (kind) {
    case 'auth':
      return 'You must be signed in to submit an answer.';
    case 'permission':
      return 'You do not have permission to submit answers for this question.';
    case 'network':
      return 'We could not reach the server. Check your connection and try again.';
    default:
      return mode === 'save'
        ? 'Something went wrong while saving your answer. Please try again.'
        : 'Something went wrong while checking your answer. Your work is not lost — please try again.';
  }
}

/**
 * Reads the page again, for a section that has noticed its close: the page
 * then draws its closed view.
 *
 * The first chapter read after the due date is the one that closes the chapter
 * on the server and grades what was saved (MaybeCloseComponent), so the page's
 * read asks for the chapter before the student's record (useComponent). This
 * once asked for the chapter on its own first (review of PR 99); with the
 * page's read in that order, that was a second chapter read, which during a
 * slow close reached the server while the close still ran and started another
 * grading pass (issue #103).
 *
 * The callback keeps one identity: the section sets its close timer again
 * whenever it changes, and a timer set again late in the minute after the
 * close could come out with no time left to wait.
 */
export function useChapterReread(courseId: string, componentId: string) {
  const queryClient = useQueryClient();

  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: componentQueryKey(courseId, componentId),
      }),
    [componentId, courseId, queryClient],
  );
}

export function useQuestionSubmission() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<QuestionSubmissionResult, ApiError, SubmitQuestionAnswerInput>({
    // By default a mutation is paused before its request while the browser says
    // it is offline. A save then waited in this page, where a closed tab lost it,
    // and the service worker's queue never saw it (issue #143). The request always
    // goes out; the worker decides what becomes of one that cannot.
    networkMode: 'offlineFirst',
    mutationFn: async (input) => {
      if (!user) {
        const error = new Error(
          'You must be signed in to submit an answer.',
        ) as ApiError;
        error.kind = 'auth';
        throw error;
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken: async (forceRefresh) => {
          try {
            return await getIdToken(forceRefresh);
          } catch (error) {
            if (navigator.onLine) {
              throw error;
            }

            // Offline, an expired token cannot be refreshed. The queue needs
            // none: its replay puts a fresh one on the request.
            return null;
          }
        },
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });
      const kuduUserId = getKuduUserId(user) ?? user.uid;
      const requestId = createRequestId();

      const result = await client.invokeKuduMethod<
        { queued?: boolean; requestId?: string } | LegacyKuduResponseEnvelope<unknown>,
        SubmissionRequestPayload
      >(
        'AddOrUpdateCourseStudentComponentV2',
        buildSubmissionRequestPayload(input, kuduUserId),
        {
          idempotencyKey: requestId,
        },
      );

      const queued =
        typeof result === 'object' &&
        result !== null &&
        'queued' in result &&
        result.queued === true;

      // A 200 is not a save. Only two answers mean the student's work is
      // safe: the stored record from the server, or "queued" from the service
      // worker. Anything else (the backend once answered a save it would not
      // store with 200 OK and nothing in it) must reach the student as a
      // failure, not as "Saved".
      if (!queued && !confirmsStoredSubmission(result)) {
        const error = new Error(
          'The server did not confirm that the submission was stored.',
        ) as ApiError;
        error.kind = 'data';
        throw error;
      }

      markPwaHomeworkEngagement();

      return {
        queued,
        requestId:
          typeof result === 'object' &&
          result !== null &&
          'requestId' in result &&
          typeof result.requestId === 'string'
            ? result.requestId
            : requestId,
      };
    },
    onSuccess: async (data, variables) => {
      // A queued (offline) submission is NOT yet persisted server-side.
      // Refetching the component now would either fail offline or overwrite the
      // student's just-entered answer with stale pre-submit server state (the
      // read cache legitimately serves the last-seen response). The refetch
      // happens when the mutation actually syncs — PwaShell invalidates the
      // component query on the 'synced' sync-status broadcast.
      if (data.queued) {
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: componentQueryKey(variables.courseId, variables.componentId),
      });
    },
  });
}
